import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import type { HotelAsyncState, HotelRoomOccupancyView, HotelTravelerOption } from '../models.js';
import { GuestSlot } from './hotel_guest_slot.js';
import {
  distribute,
  MAX_GUESTS,
  MAX_GUESTS_PER_ROOM,
  MAX_ROOMS,
  newRoom,
  newSlot,
  type RoomState,
} from './hotel_rooms_matrix.js';
import { GuestPicker, PolicyPicker, PolicyRow, RoomNote } from './hotel_rooms_pickers.js';
import { useStableCallback } from '../../shared/use_stable_callback.js';

export type HotelRoomsFieldCopy = {
  roomsLabel: string;
  travelersLabel: string;
  noTravelersLabel: string;
  travelerErrorLabel: string;
  travelerSearchPlaceholder: string;
  loadMoreTravelersLabel: string;
  roomLabel(roomNumber: number): string;
  roomSummary(roomCount: number, guestCount: number): string;
  addRoomLabel: string;
  maxRoomsLabel: string;
  chooseGuestLabel: string;
  companionLabel: string;
  addGuestLabel(roomNumber: number): string;
  removeRoomLabel(roomNumber: number): string;
  backToRoomsLabel: string;
  roomGuestHeading(roomNumber: number, companion: boolean): string;
  policyTravelerLabel: string;
  policyRequesterLabel: string;
  roomNameNote: string;
  /** O gatilho com retratos mostra só os hóspedes ("5 hóspedes"); o resumo
   *  com os quartos fica no nome acessível e no pé do campo. */
  guestCountLabel(count: number): string;
  /** O selo do primeiro hóspede do quarto, em nome de quem a reserva sai. */
  holderBadgeLabel: string;
  /** O nome acessível do ⋯ de uma vaga ("Opções de Ana Martins"). */
  guestOptionsLabel(name: string): string;
  makeHolderLabel: string;
  swapGuestLabel: string;
  removeFromRoomLabel: string;
  /** A legenda da linha "Você" na escolha de hóspede ("Ana Martins · hóspede"). */
  guestRoleLabel: string;
  /** O fechar do pé do campo ("Pronto"), no popover e na folha. */
  doneLabel: string;
  /** Optional: shown in place of `addRoomLabel` when the 9-guest global cap is
   *  reached. Absent → the button is only disabled, with no hint. */
  maxGuestsLabel?: string;
  /** Optional: the caption of a person already placed in a room, who stays
   *  visible in the guest picker but locked ("Already in room 2"). Absent →
   *  placed people are hidden from the list instead. */
  guestInRoomLabel?: (roomNumber: number) => string;
};

export type Choosing = { room: number; slot: number };

function namedTravelerIds(matrix: readonly RoomState[]): string[] {
  return matrix.flatMap((room) =>
    room.slots.flatMap((slot) => (slot.travelerId ? [slot.travelerId] : [])),
  );
}

const slotCount = (matrix: readonly RoomState[]): number =>
  matrix.reduce((total, room) => total + room.slots.length, 0);

function addRoomButtonLabel(
  roomCount: number,
  slotsFull: boolean,
  copy: HotelRoomsFieldCopy,
): string {
  if (roomCount >= MAX_ROOMS) return copy.maxRoomsLabel;
  return slotsFull ? (copy.maxGuestsLabel ?? copy.addRoomLabel) : copy.addRoomLabel;
}

function resolvedPolicy(
  travelerIds: readonly string[],
  current: string | undefined,
  requesterId: string | undefined,
): string | undefined {
  if (current && travelerIds.includes(current)) return current;
  if (requesterId && travelerIds.includes(requesterId)) return requesterId;
  return travelerIds[0];
}

function RoomCard({
  room,
  roomIndex,
  removable,
  namedFull,
  slotsFull,
  travelers,
  copy,
  disabled,
  fill,
  onChoose,
  onMakeHolder,
  onRemoveRoom,
  onRemoveSlot,
}: {
  readonly room: RoomState;
  readonly roomIndex: number;
  readonly removable: boolean;
  readonly namedFull: boolean;
  readonly slotsFull: boolean;
  readonly travelers: readonly HotelTravelerOption[];
  readonly copy: HotelRoomsFieldCopy;
  readonly disabled: boolean;
  readonly fill: boolean;
  readonly onChoose: (slot: number) => void;
  readonly onMakeHolder: (slot: number) => void;
  readonly onRemoveRoom: () => void;
  readonly onRemoveSlot: (slot: number) => void;
}): ReactNode {
  return (
    <div className="grid gap-1.5 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-caption font-medium text-muted-foreground">
          {copy.roomLabel(roomIndex + 1)}
          <RoomNote note={copy.roomNameNote} />
        </span>
        {removable && !disabled && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={copy.removeRoomLabel(roomIndex + 1)}
            onClick={onRemoveRoom}
          >
            <XIcon aria-hidden="true" />
          </Button>
        )}
      </div>
      {room.slots.map((slot, slotIndex) => (
        <GuestSlot
          key={slot.key}
          travelerId={slot.travelerId}
          slotIndex={slotIndex}
          roomNumber={roomIndex + 1}
          full={namedFull}
          travelers={travelers}
          copy={copy}
          disabled={disabled}
          removable={room.slots.length > 1 || slot.travelerId !== undefined}
          fill={fill}
          onChoose={() => {
            onChoose(slotIndex);
          }}
          onMakeHolder={() => {
            onMakeHolder(slotIndex);
          }}
          onRemove={() => {
            onRemoveSlot(slotIndex);
          }}
        />
      ))}
      {!disabled && room.slots.length < MAX_GUESTS_PER_ROOM && !slotsFull && (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-1 h-8 justify-start gap-2 font-normal text-muted-foreground hover:text-foreground"
          aria-label={copy.addGuestLabel(roomIndex + 1)}
          onClick={() => {
            onChoose(room.slots.length);
          }}
        >
          <PlusIcon aria-hidden="true" />
          <span>{copy.companionLabel}</span>
        </Button>
      )}
    </div>
  );
}

function removeSlot(matrix: RoomState[], roomIndex: number, slotIndex: number): RoomState[] {
  return matrix.map((current, index) => {
    if (index !== roomIndex) return current;
    const slots =
      current.slots.length === 1
        ? current.slots.map((slot) => ({ ...slot, travelerId: undefined }))
        : current.slots.filter((_, position) => position !== slotIndex);
    return { ...current, slots };
  });
}

/** O hóspede da vaga passa a ser o primeiro do quarto — o titular, em nome de
 *  quem a reserva sai. Os demais seguem na ordem em que estavam. */
function makeHolder(matrix: RoomState[], roomIndex: number, slotIndex: number): RoomState[] {
  return matrix.map((current, index) => {
    const moved = current.slots[slotIndex];
    if (index !== roomIndex || !moved) return current;
    return { ...current, slots: [moved, ...current.slots.filter((slot) => slot !== moved)] };
  });
}

function RoomsOverview({
  matrix,
  namedIds,
  namedFull,
  slotsFull,
  policyId,
  requesterId,
  travelers,
  copy,
  disabled,
  fill,
  policyInline,
  onChoose,
  onEmit,
  onAddRoom,
  onChoosePolicy,
}: {
  readonly matrix: RoomState[];
  readonly namedIds: readonly string[];
  readonly namedFull: boolean;
  readonly slotsFull: boolean;
  readonly policyId: string | undefined;
  readonly requesterId?: string;
  readonly travelers: readonly HotelTravelerOption[];
  readonly copy: HotelRoomsFieldCopy;
  readonly disabled: boolean;
  readonly fill: boolean;
  /** A linha da política vai no fim da lista — sem host que a ponha no pé. */
  readonly policyInline: boolean;
  readonly onChoose: (value: Choosing) => void;
  readonly onEmit: (value: RoomState[]) => void;
  readonly onAddRoom: () => void;
  readonly onChoosePolicy: () => void;
}): ReactNode {
  return (
    <div className="grid">
      <div className="scrollbar-rail [scrollbar-gutter:auto]! grid max-h-80 divide-y divide-border overflow-y-auto">
        {matrix.map((room, roomIndex) => (
          <RoomCard
            key={room.key}
            room={room}
            roomIndex={roomIndex}
            removable={matrix.length > 1}
            namedFull={namedFull}
            slotsFull={slotsFull}
            travelers={travelers}
            copy={copy}
            disabled={disabled}
            fill={fill}
            onChoose={(slot) => {
              onChoose({ room: roomIndex, slot });
            }}
            onMakeHolder={(slot) => {
              onEmit(makeHolder(matrix, roomIndex, slot));
            }}
            onRemoveRoom={() => {
              onEmit(matrix.filter((_, index) => index !== roomIndex));
            }}
            onRemoveSlot={(slot) => {
              onEmit(removeSlot(matrix, roomIndex, slot));
            }}
          />
        ))}
      </div>
      {!disabled && (
        <>
          <Separator />
          <div className="p-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-start gap-2 font-normal"
              disabled={matrix.length >= MAX_ROOMS || slotsFull}
              onClick={onAddRoom}
            >
              {matrix.length < MAX_ROOMS && !slotsFull && <PlusIcon aria-hidden="true" />}
              <span>{addRoomButtonLabel(matrix.length, slotsFull, copy)}</span>
            </Button>
          </div>
        </>
      )}
      {policyInline && namedIds.length > 1 && policyId && (
        <>
          <Separator />
          <PolicyRow
            policyId={policyId}
            requesterId={requesterId}
            travelers={travelers}
            copy={copy}
            disabled={disabled}
            onChoose={onChoosePolicy}
          />
        </>
      )}
    </div>
  );
}

/** A tela do campo: a visão geral dos quartos, a escolha de hóspede de uma
 *  vaga ou a escolha de quem rege a política. */
export type HotelRoomsView =
  | { kind: 'rooms' }
  | { kind: 'guest'; choosing: Choosing }
  | { kind: 'policy' };

/** Onde a pessoa escolhida entra na matriz; `undefined` quando o teto veta. */
function placeGuest(
  matrix: RoomState[],
  choosing: Choosing,
  travelerId: string,
  caps: { readonly namedFull: boolean; readonly slotsFull: boolean },
): RoomState[] | undefined {
  const slots = matrix[choosing.room]?.slots ?? [];
  const isNewSlot = choosing.slot >= slots.length;
  if (
    isNewSlot ? caps.slotsFull : caps.namedFull && slots[choosing.slot]?.travelerId === undefined
  ) {
    return undefined;
  }
  return matrix.map((room, roomIndex) => {
    if (roomIndex !== choosing.room) return room;
    const next = room.slots.map((slot, slotIndex) =>
      slotIndex === choosing.slot ? { ...slot, travelerId } : slot,
    );
    if (choosing.slot >= next.length) next.push(newSlot(travelerId));
    return { ...room, slots: next };
  });
}

/** A tela do campo: controlada pelo host quando ele passa o par, local senão.
 *  `reset` volta à visão geral com identidade estável, para o efeito de
 *  redistribuir — o `onViewChange` do host muda a cada render e vai por
 *  `useStableCallback`. */
function useRoomsView(
  controlled: HotelRoomsView | undefined,
  onChange: ((view: HotelRoomsView) => void) | undefined,
): [HotelRoomsView, (view: HotelRoomsView) => void, () => void] {
  const [own, setOwn] = useState<HotelRoomsView>({ kind: 'rooms' });
  const notify = useStableCallback((next: HotelRoomsView) => {
    onChange?.(next);
  });
  const set = useCallback(
    (next: HotelRoomsView) => {
      setOwn(next);
      notify(next);
    },
    [notify],
  );
  const reset = useCallback(() => {
    set({ kind: 'rooms' });
  }, [set]);
  return [controlled ?? own, set, reset];
}

export type HotelRoomsFieldProps = {
  readonly rooms: readonly HotelRoomOccupancyView[];
  readonly travelerIds: readonly string[];
  readonly policyTravelerId?: string;
  readonly requesterId?: string;
  readonly travelers: readonly HotelTravelerOption[];
  readonly travelerSearchState: HotelAsyncState;
  readonly hasMoreTravelers?: boolean;
  readonly copy: HotelRoomsFieldCopy;
  readonly disabled?: boolean;
  readonly onChange: (value: {
    rooms: readonly HotelRoomOccupancyView[];
    travelerIds: readonly string[];
    policyTravelerId?: string;
  }) => void;
  readonly onTravelerQueryChange: (query: string) => void;
  readonly onLoadMoreTravelers: () => void;
  /** Toma a largura do pai em vez dos 19rem do popover — o miolo dentro de uma
   *  folha ou cartão do celular (#2653), que já vai de borda a borda. */
  readonly fill?: boolean;
  /** A tela, controlada por quem desenha o pé do campo (`HotelRoomsPickerField`):
   *  a política se escolhe pela linha do pé, e o pé some fora da visão geral.
   *  Com o par controlado a linha da política sai da lista. Ausente ⇒ o campo
   *  guarda a tela e põe a política no fim da lista. */
  readonly view?: HotelRoomsView;
  readonly onViewChange?: (view: HotelRoomsView) => void;
};

/**
 * O campo QUEM do hotel: a matriz de quartos e vagas, a escolha de hóspede por
 * vaga (drill-in com a busca do diretório) e a política. As três telas usam o
 * `TravelerPicker` compartilhado com o aéreo — a mesma linha de pessoa, o mesmo
 * cabeçalho de voltar, a mesma legenda de bloqueio. "Adicionar quarto" já abre
 * a escolha do primeiro hóspede do quarto novo: a vaga vazia sem nome era um
 * passo a mais para o gesto mais comum.
 */
export function HotelRoomsField({
  rooms,
  travelerIds,
  policyTravelerId,
  requesterId,
  travelers,
  travelerSearchState,
  hasMoreTravelers = false,
  copy,
  disabled = false,
  onChange,
  onTravelerQueryChange,
  onLoadMoreTravelers,
  fill = false,
  view: controlledView,
  onViewChange,
}: HotelRoomsFieldProps) {
  const [matrix, setMatrix] = useState<RoomState[]>(() => distribute(rooms, travelerIds));
  const [view, setView, resetView] = useRoomsView(controlledView, onViewChange);
  const [travelerQuery, setTravelerQuery] = useState('');
  // Chave do que veio do host: só um novo `rooms`/`travelerIds` re-distribui a matriz.
  const incomingKey = JSON.stringify([rooms, travelerIds]);
  const previousIncomingKey = useRef(incomingKey);
  // O que ESTE campo acabou de emitir: o host controlado o devolve como prop, e
  // sem reconhecer o eco o efeito abaixo fecharia a tela que a MESMA ação abriu
  // (escolha do 1º hóspede do quarto novo). Uma mudança externa depois disso
  // esquece a chave: o host voltar por fora ao mesmo valor também redistribui.
  const lastEmittedKey = useRef<string | undefined>(undefined);
  const namedIds = namedTravelerIds(matrix);
  // DOIS tetos, não um: `namedFull` impede NOMEAR mais alguém (o wire é
  // `.max(9)`), `slotsFull` impede CRIAR vaga. Contar só nomes deixava
  // "adicionar quarto" abrir vaga anônima acima de 9 — e como a matriz muda
  // antes do `onChange`, o host não tinha como vetar.
  const namedFull = namedIds.length >= MAX_GUESTS;
  const slotsFull = slotCount(matrix) >= MAX_GUESTS;

  useEffect(() => {
    if (incomingKey === previousIncomingKey.current) return;
    previousIncomingKey.current = incomingKey;
    if (incomingKey === lastEmittedKey.current) return;
    lastEmittedKey.current = undefined;
    setMatrix(distribute(rooms, travelerIds));
    resetView();
    setTravelerQuery('');
  }, [incomingKey, rooms, travelerIds, resetView]);

  const valueOf = (next: readonly RoomState[], policy?: string) => {
    const nextTravelerIds = namedTravelerIds(next);
    const nextPolicy = policy ?? resolvedPolicy(nextTravelerIds, policyTravelerId, requesterId);
    return {
      rooms: next.map((room) => ({ guests: Math.max(1, room.slots.length) })),
      travelerIds: nextTravelerIds,
      ...(nextPolicy ? { policyTravelerId: nextPolicy } : {}),
    };
  };

  const emit = (next: RoomState[]) => {
    const value = valueOf(next);
    setMatrix(next);
    lastEmittedKey.current = JSON.stringify([value.rooms, value.travelerIds]);
    onChange(value);
  };
  const resetQuery = () => {
    onTravelerQueryChange('');
    setTravelerQuery('');
  };
  const showRooms = () => {
    resetQuery();
    setView({ kind: 'rooms' });
  };
  const chooseGuest = (choosing: Choosing) => {
    resetQuery();
    setView({ kind: 'guest', choosing });
  };

  const width = fill ? 'w-full' : 'w-[19rem]';

  if (view.kind === 'guest') {
    return (
      <div className={width}>
        <GuestPicker
          choosing={view.choosing}
          matrix={matrix}
          travelers={travelers}
          travelerSearchState={travelerSearchState}
          hasMoreTravelers={hasMoreTravelers}
          copy={copy}
          fill={fill}
          requesterId={requesterId}
          query={travelerQuery}
          onQueryChange={(query) => {
            setTravelerQuery(query);
            onTravelerQueryChange(query);
          }}
          onBack={showRooms}
          onPick={(travelerId) => {
            const next = placeGuest(matrix, view.choosing, travelerId, { namedFull, slotsFull });
            if (next) emit(next);
            showRooms();
          }}
          onLoadMoreTravelers={onLoadMoreTravelers}
        />
      </div>
    );
  }

  const policyId = resolvedPolicy(namedIds, policyTravelerId, requesterId);

  if (view.kind === 'policy') {
    return (
      <div className={width}>
        <PolicyPicker
          namedIds={namedIds}
          policyId={policyId}
          travelers={travelers}
          copy={copy}
          fill={fill}
          onBack={showRooms}
          onPick={(travelerId) => {
            onChange(valueOf(matrix, travelerId));
            showRooms();
          }}
        />
      </div>
    );
  }

  return (
    <div className={width}>
      <RoomsOverview
        matrix={matrix}
        namedIds={namedIds}
        namedFull={namedFull}
        slotsFull={slotsFull}
        policyId={policyId}
        requesterId={requesterId}
        travelers={travelers}
        copy={copy}
        disabled={disabled}
        fill={fill}
        policyInline={onViewChange === undefined}
        onChoose={chooseGuest}
        onEmit={emit}
        onAddRoom={() => {
          // O quarto novo nasce e a escolha do 1º hóspede dele já abre.
          emit([...matrix, newRoom()]);
          chooseGuest({ room: matrix.length, slot: 0 });
        }}
        onChoosePolicy={() => {
          setView({ kind: 'policy' });
        }}
      />
    </div>
  );
}
